import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { CardTableArea } from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { ROUTES } from '@/routes/paths';
import { cn } from '@/lib/utils';
import { roleLabel, tones } from '@/theme';
import { leadStatus, orderStatus } from '@/theme/status';
import { EnlaceInterno, EnlacesAyuda, ListaNotas, ListaPasos, SubTitulo } from './help-blocks';
import {
  AVISO_GUIA,
  ESTADOS_LEAD,
  ESTADOS_ORDEN,
  ESTADOS_QUE_ACEPTAN_GUIA,
  ESTADOS_QUE_CUENTAN_COMO_VENTA,
  FLUJO_ORDEN,
  MOTIVOS_ENVIO,
  NOTAS_LEAD,
  NOTAS_ORDEN_ESTADOS,
  NOTAS_ROLES,
  NOTA_GUIA_EQUIVOCADA,
  NOTA_WHATSAPP_PENDIENTE,
  PASOS_GUIA,
  PASOS_PUESTA_EN_MARCHA,
  PASOS_WHATSAPP,
  PERMISOS,
  PROBLEMAS,
  REGLAS_LEAD,
  REQUISITOS_WHATSAPP,
  ROLES,
  RUTINA_DIARIA,
  pantallasEnOrdenDelMenu,
} from './help-content';

/**
 * Los ocho cuerpos de la guía.
 *
 * Cada componente es UNA sección y no tiene lógica: recorre los datos de `help-content.ts` y los
 * pinta con las primitivas del sistema de diseño. Los estados de órdenes y leads se pintan con
 * `orderStatus` / `leadStatus` de `theme/status.ts` (etiqueta y color vienen del mismo sitio que en
 * el resto del panel), así que la guía no puede quedar con un texto que contradiga la pantalla.
 */

/** 1. Poner el negocio en marcha. */
export function SeccionPrimerosPasos() {
  return (
    <>
      <p className="text-caption text-ink-muted">
        El orden importa: cada paso deja listo lo que el siguiente necesita. Si algo falla más
        adelante, casi siempre es porque un paso anterior quedó a medias.
      </p>

      <ol className="space-y-4">
        {PASOS_PUESTA_EN_MARCHA.map((paso, indice) => (
          <li key={paso.titulo} className="flex gap-3">
            <span
              aria-hidden="true"
              className="tabular flex size-7 shrink-0 items-center justify-center rounded-pill bg-brand-50 text-caption font-semibold text-brand-700"
            >
              {indice + 1}
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-ink">{paso.titulo}</p>
              <p className="mt-1 text-caption text-ink-muted">{paso.detalle}</p>
              <Link
                to={paso.to}
                className="mt-1.5 inline-block text-caption font-medium text-brand-700 hover:underline"
              >
                {`${paso.cta} →`}
              </Link>
            </div>
          </li>
        ))}
      </ol>

      <Alert tone="info">{RUTINA_DIARIA}</Alert>
    </>
  );
}

/** 2. Qué hace cada pantalla (el índice con las rutas reales). */
export function SeccionPantallas() {
  const pantallas = pantallasEnOrdenDelMenu();

  return (
    <>
      <p className="text-caption text-ink-muted">
        Las pantallas en el mismo orden del menú lateral. Cada ruta se puede abrir desde acá y
        también pegarse en el navegador: son las direcciones reales del panel.
      </p>

      <div className="space-y-4">
        {pantallas.map((pantalla) => {
          const Icon = pantalla.icon;

          return (
            <article key={pantalla.to} className="rounded-panel border border-line p-4 sm:p-5">
              <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600">
                  <Icon size={16} />
                </span>

                <h4 className="text-body font-semibold text-ink">{pantalla.label}</h4>

                <code className="rounded-control bg-surface-muted px-1.5 py-0.5 font-mono text-micro text-ink-soft">
                  {pantalla.to}
                </code>

                <Link
                  to={pantalla.to}
                  className="ml-auto text-caption font-medium text-brand-700 hover:underline"
                >
                  Abrir →
                </Link>
              </header>

              {pantalla.contenido ? (
                <>
                  <p className="mt-2 text-caption text-ink-muted">{pantalla.contenido.resumen}</p>

                  <ul className="mt-3 space-y-2">
                    {pantalla.contenido.detalles.map((detalle) => (
                      <li key={detalle} className="flex gap-2.5 text-caption text-ink-muted">
                        <span
                          aria-hidden="true"
                          className="mt-1.5 size-1.5 shrink-0 rounded-pill bg-brand-600"
                        />
                        <span className="min-w-0 flex-1">{detalle}</span>
                      </li>
                    ))}
                  </ul>

                  <div
                    className={cn(
                      'mt-3 flex items-start gap-2 text-caption font-medium',
                      tones[pantalla.contenido.escritura.tone].text,
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-1.5 size-1.5 shrink-0 rounded-pill',
                        tones[pantalla.contenido.escritura.tone].dot,
                      )}
                    />
                    <span className="min-w-0 flex-1">{pantalla.contenido.escritura.texto}</span>
                  </div>
                </>
              ) : (
                <p className="mt-2 text-caption text-ink-muted">
                  Todavía no hay guía escrita para esta pantalla.
                </p>
              )}
            </article>
          );
        })}
      </div>
    </>
  );
}

/** 3. WhatsApp de punta a punta. */
export function SeccionWhatsApp() {
  return (
    <>
      <p className="text-caption text-ink-muted">
        WhatsApp se conecta en dos sitios a la vez: las credenciales viven en el panel
        (Integraciones) y el webhook en la app de Meta. Los dos lados tienen que apuntarse entre sí.
      </p>

      <ListaPasos pasos={PASOS_WHATSAPP} />

      <SubTitulo>Qué necesita cada cosa para funcionar</SubTitulo>

      <CardTableArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Para…</TableHeaderCell>
              <TableHeaderCell>Hace falta</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {REQUISITOS_WHATSAPP.map((requisito) => (
              <TableRow key={requisito.para}>
                <TableCell className="align-top font-medium text-ink">{requisito.para}</TableCell>
                <TableCell className="align-top">{requisito.necesita}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardTableArea>

      <Alert tone="info">{NOTA_WHATSAPP_PENDIENTE}</Alert>

      <div className="grid gap-3 sm:grid-cols-2">
        <EnlaceInterno
          to={ROUTES.integrations}
          icon={<span className="text-caption font-semibold">IA</span>}
          title="Claves y canales"
          description="DeepSeek, Gemini y los cuatro canales, con conectar, probar y desconectar."
        />
        <EnlaceInterno
          to={ROUTES.templates}
          icon={<span className="text-caption font-semibold">24 h</span>}
          title="Plantillas de WhatsApp"
          description="Lo que se puede mandar cuando la ventana de 24 horas ya se cerró."
        />
      </div>
    </>
  );
}

/** 4. La guía de envío (el despacho y el aviso al cliente). */
export function SeccionGuia() {
  return (
    <>
      <Alert tone="warning">{AVISO_GUIA}</Alert>

      <ListaPasos pasos={PASOS_GUIA} />

      <SubTitulo>Estados que aceptan la guía</SubTitulo>

      <p className="flex flex-wrap items-center gap-2">
        {ESTADOS_QUE_ACEPTAN_GUIA.map((estado) => (
          <StatusBadge
            key={estado}
            label={orderStatus[estado].label}
            tone={orderStatus[estado].tone}
          />
        ))}
      </p>

      <SubTitulo>Qué significa el resultado del envío</SubTitulo>

      <CardTableArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Resultado</TableHeaderCell>
              <TableHeaderCell>Qué significa</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MOTIVOS_ENVIO.map((motivo) => (
              <TableRow key={motivo.motivo}>
                <TableCell className="align-top">
                  <Badge tone={motivo.tone} dot>
                    {motivo.motivo}
                  </Badge>
                </TableCell>
                <TableCell className="align-top">{motivo.significa}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardTableArea>

      <Alert tone="info">{NOTA_GUIA_EQUIVOCADA}</Alert>

      <EnlacesAyuda
        enlaces={[
          { label: 'Órdenes', to: ROUTES.orders },
          { label: 'Conversaciones', to: ROUTES.conversations },
        ]}
      />
    </>
  );
}

/** 5. Estados de una orden. */
export function SeccionOrdenes() {
  return (
    <>
      <p className="text-caption text-ink-muted">
        El recorrido normal de una orden es una línea; la cancelación sale del flujo y se puede hacer
        desde cualquier estado que no sea final. Los colores y las etiquetas son los mismos que usa
        la pantalla de Órdenes.
      </p>

      <p className="flex flex-wrap items-center gap-x-2 gap-y-3">
        {FLUJO_ORDEN.map((estado, indice) => (
          <span key={estado} className="flex items-center gap-2">
            <StatusBadge label={orderStatus[estado].label} tone={orderStatus[estado].tone} />
            {indice < FLUJO_ORDEN.length - 1 ? (
              <span aria-hidden="true" className="text-ink-faint">
                →
              </span>
            ) : null}
          </span>
        ))}

        <span className="flex items-center gap-2">
          <span className="text-caption text-ink-faint">o</span>
          <StatusBadge label={orderStatus.CANCELADA.label} tone={orderStatus.CANCELADA.tone} />
        </span>
      </p>

      <CardTableArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Estado</TableHeaderCell>
              <TableHeaderCell>Qué significa</TableHeaderCell>
              <TableHeaderCell>De acá se puede pasar a</TableHeaderCell>
              <TableHeaderCell>Quién lo hace</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ESTADOS_ORDEN.map((item) => (
              <TableRow key={item.estado}>
                <TableCell className="align-top">
                  <StatusBadge
                    label={orderStatus[item.estado].label}
                    tone={orderStatus[item.estado].tone}
                  />
                </TableCell>
                <TableCell className="align-top">{item.significado}</TableCell>
                <TableCell className="align-top text-caption text-ink-muted">
                  {item.hacia.length === 0
                    ? 'Es un estado final: no cambia más.'
                    : item.hacia.map((estado) => orderStatus[estado].label).join(' · ')}
                </TableCell>
                <TableCell className="align-top text-caption text-ink-muted">{item.quien}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardTableArea>

      <ListaNotas notas={NOTAS_ORDEN_ESTADOS} />

      <SubTitulo>Qué cuenta como venta</SubTitulo>

      <p className="flex flex-wrap items-center gap-2">
        {ESTADOS_QUE_CUENTAN_COMO_VENTA.map((estado) => (
          <StatusBadge key={estado} label={orderStatus[estado].label} tone={orderStatus[estado].tone} />
        ))}
      </p>

      <p className="text-caption text-ink-muted">
        Los ingresos, el ticket promedio y el CPA se calculan con esas cuatro. «Pendiente de
        confirmación» todavía no es una venta y «Cancelada» no lo es: por eso una orden pendiente
        aparece en el resumen, pero no suma a los ingresos.
      </p>

      <EnlacesAyuda
        enlaces={[
          { label: 'Órdenes', to: ROUTES.orders },
          { label: 'Reportes', to: ROUTES.reports },
        ]}
      />
    </>
  );
}

/** 6. Estados de un lead (el embudo del CRM). */
export function SeccionLeads() {
  return (
    <>
      <p className="text-caption text-ink-muted">
        Un lead es una conversación, y su estado se mueve solo: el sistema lo reclasifica cada vez que
        el cliente escribe. La única forma de que se quede quieto es moverlo a mano.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {ESTADOS_LEAD.map((item) => (
          <div key={item.estado} className="rounded-panel border border-line p-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge label={leadStatus[item.estado].label} tone={leadStatus[item.estado].tone} />
              <span className="text-caption text-ink-faint">{item.clave}</span>
            </div>

            <p className="mt-2 text-caption text-ink-muted">{item.significado}</p>
          </div>
        ))}
      </div>

      <SubTitulo>Cómo lo clasifica el sistema (aplica la primera regla que se cumple)</SubTitulo>

      <CardTableArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell className="w-10">#</TableHeaderCell>
              <TableHeaderCell>Si…</TableHeaderCell>
              <TableHeaderCell>Queda en</TableHeaderCell>
              <TableHeaderCell>Motivo guardado</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {REGLAS_LEAD.map((regla, indice) => (
              <TableRow key={regla.motivo}>
                <TableCell className="tabular align-top text-caption text-ink-faint">
                  {indice + 1}
                </TableCell>
                <TableCell className="align-top">{regla.condicion}</TableCell>
                <TableCell className="align-top">
                  <StatusBadge
                    label={leadStatus[regla.estado].label}
                    tone={leadStatus[regla.estado].tone}
                  />
                </TableCell>
                <TableCell className="align-top font-mono text-micro text-ink-soft">
                  {regla.motivo}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardTableArea>

      <ListaNotas notas={NOTAS_LEAD} />

      <EnlacesAyuda enlaces={[{ label: 'CRM', to: ROUTES.crm }]} />
    </>
  );
}

/** 7. Quién puede hacer qué. */
export function SeccionRoles() {
  return (
    <>
      <p className="text-caption text-ink-muted">
        Cada negocio tiene sus propios roles, y el rol decide qué se puede LEER y qué se puede
        ESCRIBIR. Nadie queda afuera de la información: lo que cambia son los botones de acción.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {ROLES.map((item) => (
          <div key={item.rol} className="rounded-panel border border-line p-4">
            <Badge tone={item.rol === 'ADMIN' ? 'brand' : 'info'} dot>
              {roleLabel[item.rol]}
            </Badge>
            <p className="mt-2 text-caption text-ink-muted">{item.quePuede}</p>
          </div>
        ))}
      </div>

      <SubTitulo>Pantalla por pantalla</SubTitulo>

      <CardTableArea>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Pantalla</TableHeaderCell>
              <TableHeaderCell>Quién puede ver</TableHeaderCell>
              <TableHeaderCell>Quién puede escribir</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PERMISOS.map((permiso) => (
              <TableRow key={permiso.to}>
                <TableCell className="align-top">
                  <Link
                    to={permiso.to}
                    className="font-medium text-ink whitespace-nowrap hover:text-brand-700 hover:underline"
                  >
                    {permiso.pantalla}
                  </Link>
                </TableCell>
                <TableCell className="align-top text-caption text-ink-muted">{permiso.ver}</TableCell>
                <TableCell className="align-top">
                  <span
                    className={cn(
                      'flex items-start gap-2 text-caption font-medium',
                      tones[permiso.escribir.tone].text,
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-1.5 size-1.5 shrink-0 rounded-pill',
                        tones[permiso.escribir.tone].dot,
                      )}
                    />
                    <span className="min-w-0 flex-1">{permiso.escribir.texto}</span>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardTableArea>

      <ListaNotas notas={NOTAS_ROLES} />
    </>
  );
}

/** 8. Problemas típicos. */
export function SeccionProblemas() {
  return (
    <>
      <p className="text-caption text-ink-muted">
        Las dudas que aparecen una y otra vez, con la causa real. Si nada de esto explica lo que ves,
        casi siempre es una credencial sin conectar o una ventana de 24 horas cerrada.
      </p>

      <div className="space-y-4">
        {PROBLEMAS.map((problema) => (
          <article key={problema.pregunta} className="rounded-panel border border-line p-4">
            <h4 className="text-body font-semibold text-ink">{problema.pregunta}</h4>
            <p className="mt-1.5 text-caption text-ink-muted">{problema.respuesta}</p>
            <EnlacesAyuda enlaces={problema.enlaces} />
          </article>
        ))}
      </div>
    </>
  );
}